import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Drawer,
  Group,
  Image,
  Menu,
  Modal,
  Popover,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { IconCalendar, IconTrash, IconUsersGroup } from "@tabler/icons-react";
import { IconUser } from "@tabler/icons-react";
import { IconUsers } from "@tabler/icons-react";
import React, { useEffect, useState, useCallback } from "react";
import useUserStore from "../stores/userStore";
import fetchApi from "../components/Lib/api";
import useStartupStore from "../stores/startupStore";

const EditCategoryDrawer = ({
  drawerOpened,
  setDrawerOpened,
  selectedCategory,
  setSelectedCategory,
  isNewCategory,
  setIsNewCategory
}) => {
  const { program, setProgram, setCategory, slug } = useStartupStore((state) => ({
    program: state.program,
    setProgram: state.setProgram,
    setCategory: state.setCategory,
    slug: state.slug,
  }));
  const { is_notion_connected } = useUserStore((state) => ({
    is_notion_connected: state.is_notion_connected,
  }));
  const [notionLinkStatus, setNotionLinkStatus] = useState("idle"); // idle, linking, linked, error
  const [notionPageId, setNotionPageId] = useState(
    selectedCategory?.notion_page_id
  );

  const [categoryName, setCategoryName] = useState(selectedCategory?.name);
  const [subcategoryNames, setSubcategoryNames] = useState(selectedCategory?.sub_categories.map((s) => ({
    name: s.name,
    id: s.id,
    is_active: s.is_active,
    is_editable: s.is_editable,
    privacy: s.privacy || "Everyone",
    start_date: s.start_day ? new Date(s.start_day) : null,
    end_date: s.end_day ? new Date(s.end_day) : null,
  })));
  const [newSubcategoryNames, setNewSubcategoryNames] = useState([]);
  const [subcategoriesToDelete, setSubcategoriesToDelete] = useState([]);
  const [error, setError] = useState(null);
  // Date state
  const [newStartDate, setNewStartDate] = useState(selectedCategory?.start_date ? new Date(selectedCategory?.start_date) : null);
  const [newEndDate, setNewEndDate] = useState(selectedCategory?.end_date ? new Date(selectedCategory?.end_date) : null);
  const [notionModalOpen, setNotionModalOpen] = useState(false);

  const [
    deleteConfirmOpened,
    { open: openDeleteConfirm, close: closeDeleteConfirm },
  ] = useDisclosure(false);


  useEffect(() => {
    if (selectedCategory) {
      setCategoryName(selectedCategory.name);
      setSubcategoryNames(
        selectedCategory.sub_categories.map((s) => ({
          name: s.name,
          id: s.id,
          is_active: s.is_active,
          is_editable: s.is_editable,
          privacy: s.privacy || "Everyone",
          start_date: s.start_day ? new Date(s.start_day) : null,
          end_date: s.end_day ? new Date(s.end_day) : null,
        }))
      );
      setNewStartDate(selectedCategory.start_date ? new Date(selectedCategory.start_date) : null);
      setNewEndDate(selectedCategory.end_date ? new Date(selectedCategory.end_date) : null);
      setNotionPageId(selectedCategory.notion_page_id);
    } else if (isNewCategory) {
      // Reset for new category
      setCategoryName("");
      setSubcategoryNames([]);
      setNewStartDate(null);
      setNewEndDate(null);
      setNotionPageId(null);
    }
  }, [selectedCategory, isNewCategory]);
  

  useEffect(() => {
    setSubcategoryNames((subs) =>
      subs?.map((s) => {
        const subStart = s.start_date?.getTime() ?? null;
        const subEnd = s.end_date?.getTime() ?? null;
        const catStart = newStartDate?.getTime() ?? null;
        const catEnd = newEndDate?.getTime() ?? null;

        let startDate = s.start_date;
        let endDate = s.end_date;

        // 1) clamp start_date up to catStart
        if (catStart !== null && subStart !== null && subStart < catStart) {
          startDate = new Date(newStartDate);
        }
        // 2) clamp end_date down to catEnd
        if (catEnd !== null && subEnd !== null && subEnd > catEnd) {
          endDate = new Date(newEndDate);
        }
        // 3) clamp end_date up to catStart (no end before category start)
        if (catStart !== null && subEnd !== null && subEnd < catStart) {
          endDate = new Date(newStartDate);
        }
        // 4) (optional) clamp start_date down to catEnd (no start after category end)
        if (catEnd !== null && subStart !== null && subStart > catEnd) {
          startDate = new Date(newEndDate);
        }

        return { ...s, start_date: startDate, end_date: endDate };
      })
    );

    setNewSubcategoryNames((subs) =>
      subs?.map((s) => {
        const subStart = s.start_date?.getTime() ?? null;
        const subEnd = s.end_date?.getTime() ?? null;
        const catStart = newStartDate?.getTime() ?? null;
        const catEnd = newEndDate?.getTime() ?? null;

        let startDate = s.start_date;
        let endDate = s.end_date;

        // same four clamps for new subcategories
        if (catStart !== null && subStart !== null && subStart < catStart) {
          startDate = new Date(newStartDate);
        }
        if (catEnd !== null && subEnd !== null && subEnd > catEnd) {
          endDate = new Date(newEndDate);
        }
        if (catStart !== null && subEnd !== null && subEnd < catStart) {
          endDate = new Date(newStartDate);
        }
        if (catEnd !== null && subStart !== null && subStart > catEnd) {
          startDate = new Date(newEndDate);
        }

        return { ...s, start_date: startDate, end_date: endDate };
      })
    );
  }, [newStartDate, newEndDate]);


  const handleSubcategoryNameChange = (index, event, isNew = false) => {
    const handler = isNew ? setNewSubcategoryNames : setSubcategoryNames;
    handler((prev) => {
      const newSubcategories = [...prev];
      newSubcategories[index] = {
        ...newSubcategories[index],
        name: event.target.value,
      };
      return newSubcategories;
    });
  };

  const handleSubcategoryPrivacyChange = (index, value, isNew = false) => {
    const handler = isNew ? setNewSubcategoryNames : setSubcategoryNames;
    handler((prev) => {
      const newSubcategories = [...prev];
      newSubcategories[index] = {
        ...newSubcategories[index],
        privacy: value,
      };
      return newSubcategories;
    });
  };

  const handleSubcategoryDateChange = (index, type, date, isNew = false) => {
    const handler = isNew ? setNewSubcategoryNames : setSubcategoryNames;
    handler((prev) => {
      const newSubcategories = [...prev];
      newSubcategories[index] = {
        ...newSubcategories[index],
        [type]: date,
      };
      return newSubcategories;
    });
  };

  const getPrivacyColor = (privacy) => {
    switch (privacy) {
      case "Everyone":
        return "blue";
      case "Only me":
        return "green";
      case "Members":
        return "violet";
      default:
        return "gray";
    }
  };
  const handleDeleteCategory = async () => {
    if (!selectedCategory || isNewCategory) return;

    openDeleteConfirm();
  };

  // Handlers for category management
  const handleAddNewField = () => {
    setNewSubcategoryNames((prev) => [
      ...prev,
      { name: "", privacy: "Everyone", start_date: null, end_date: null },
    ]);
  };

  const handleRemoveNewField = (indexToRemove) => {
    setNewSubcategoryNames((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const confirmDeleteCategory = async () => {
    if (!selectedCategory || isNewCategory) return;

    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/categories/${selectedCategory.id}`,
        "DELETE"
      );

      if (response.ok) {
        // Remove the category from the program
        const updatedCategories = program.categories.filter(
          (cat) => cat.id !== selectedCategory.id
        );

        setProgram({
          ...program,
          categories: updatedCategories,
        });

        // Close the drawer and reset state
        closeDeleteConfirm();
        resetDrawerState();
      } else {
        const msg = (await response.json()).error;
        console.error(msg);
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };
  const redirectToNotionAuth = () => {
    const currentUrl = window.location.href; // where the user is now
    window.location.href = `/auth/notion?return_url=${encodeURIComponent(
      currentUrl
    )}`;
  };
  // Handler for linking category to notion
  const handleLinkToNotion = async () => {
    setNotionLinkStatus("linking");
    try {
      const response = await fetchApi(
        `/notion/link_category/${selectedCategory.id}`,
        "POST"
      );

      if (response.ok) {
        const data = await response.json();
        setNotionPageId(data.notion_page_id);
        // console.log("notion page linked with id ",notionPageId)
        setNotionLinkStatus("linked");
      } else {
        setNotionLinkStatus("error");
      }
    } catch (err) {
      setNotionLinkStatus("error");
    }
  };

  const handleUpdate = async () => {
    // Build the category data from the form states
    const categoryData = {
      name: categoryName,
      start_date: newStartDate,
      end_date: newEndDate,
      sub_categories_attributes: subcategoryNames.map(
        ({ is_active, is_editable, ...rest }) => ({
          ...rest,
          start_day: rest.start_date,
          end_day: rest.end_date,
        })
      ),
      new_sub_categories_attributes: newSubcategoryNames
        .filter((sub) => sub.name.trim() !== "")
        .map(({ name, privacy, start_date, end_date }) => ({
          name,
          privacy,
          start_day: start_date,
          end_day: end_date,
        })),
      deleted_sub_category_ids: subcategoriesToDelete,
    };

    try {
      if (isNewCategory) {
        if (newSubcategoryNames.length === 0) {
          setError("You must add at least one subcategory!");
          return;
        }
        // For new category, make a POST request to create the category
        const response = await fetchApi(
          `/startup_entities/${slug}/categories`,
          "POST",
          {
            category: categoryData,
          }
        );
        if (response.ok) {
          const res = await response.json();

          setCategory(res.category);
          resetDrawerState();
        } else {
          const msg = (await response.json()).error;
          console.error(msg);
        }
      } else if (selectedCategory) {
        // For existing category update - first update the dates
        try {
          const dateResponse = await fetchApi(
            `/categories/update_dates/${selectedCategory.id}`,
            "PATCH",
            { category: { start_date: newStartDate, end_date: newEndDate } }
          );

          if (!dateResponse.ok) {
            const msg = (await dateResponse.json()).error;
            console.error(msg);
            return; // Stop if date update fails
          }
        } catch (error) {
          console.error("Error during date change:", error);
          return; // Stop if date update fails
        }

        // Then update names and subcategories
        const response = await fetchApi(
          `/categories/update_names/${selectedCategory.id}`,
          "PATCH",
          { category: categoryData }
        );
        if (response.ok) {
          const res = await response.json();
          setCategory(res.category);
          resetDrawerState();
        } else {
          const msg = (await response.json()).error;
          console.error(msg);
        }
      }
    } catch (error) {
      console.error("Error during update:", error);
    }
  };

  const handleCategoryNameChange = (event) => {
    setCategoryName(event.target.value);
  };
  
  const handleSubcategoryDelete = useCallback(
    (index) => {
      setSubcategoriesToDelete((prev) => [...prev, subcategoryNames[index].id]);
      setSubcategoryNames((prev) => prev.filter((_, idx) => idx !== index));
    },
    [subcategoryNames]
  );

  const resetDrawerState = () => {
    setDrawerOpened(false);
    setSelectedCategory(null);
    setIsNewCategory(false);
    setCategoryName("");
    setSubcategoryNames([]);
    setNewSubcategoryNames([]);
    setSubcategoriesToDelete([]);
    setNewStartDate(null);
    setNewEndDate(null);
    setError(null);
  };
  return (
    <>
      <Drawer
        opened={drawerOpened}
        onClose={resetDrawerState}
        title={isNewCategory ? "Add New Category" : "Edit Category"}
        position="right"
        size="xl"
        padding="xl"
        styles={{
          title: {
            fontSize: "1.5rem",
            fontWeight: 600,
          },
        }}
      >
        {/* Notion Warning Modal */}
        <Modal
          opened={notionModalOpen}
          onClose={() => setNotionModalOpen(false)}
          centered
          withCloseButton
          size="lg"
          overlayProps={{ opacity: 0.55, blur: 3 }}
        >
          <Stack align="center" spacing="lg">
            <Text size="xl" weight={600} ta="center" className="text-gray-800">
              Important: Notion Projects Page Required
            </Text>

            <Text size="sm" ta="left" className="text-gray-600 max-w-md">
              Please make sure your Notion workspace contains the{" "}
              <strong>“Projects”</strong> template. This is required for
              integration to work properly. On the next screen, you should
              select and authorize NowCollab with this page. If the page is not
              there, please create it and try connecting again.
            </Text>

            <Image
              src="/notion_project_missing.png"
              alt="Notion Project Missing"
              w={350}
              h="auto"
              fit="contain"
              radius="md"
              className="shadow-md border"
            />

            <Group position="right" className="w-full mt-4">
              <Button
                variant="light"
                color="red"
                onClick={() => setNotionModalOpen(false)}
                className="px-6"
              >
                Cancel
              </Button>
              <Button onClick={redirectToNotionAuth} className="px-6">
                Proceed
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Alert
          title="Link to Notion"
          color="blue"
          withCloseButton={false}
          mb="xl"
          icon={<img src="/notion_logo.svg" alt="Notion" className="w-5 h-5" />}
          styles={{
            title: { fontSize: "1.05rem", fontWeight: 600 },
            message: { fontSize: "0.95rem" },
          }}
        >
          You can link this category to Notion if you have a{" "}
          <strong>Projects</strong> page in your Notion workspace.
          <Group mt="sm">
            {!is_notion_connected ? (
              <Button
                onClick={() => setNotionModalOpen(true)}
                size="xs"
                compact
                variant="filled"
                color="blue"
              >
                Connect Notion
              </Button>
            ) : (
              <Button
                onClick={handleLinkToNotion}
                disabled={notionPageId !== null}
                size="xs"
                compact
                variant={notionPageId ? "light" : "filled"}
                color={
                  notionPageId
                    ? "gray"
                    : notionLinkStatus === "error"
                    ? "red"
                    : "blue"
                }
                loading={notionLinkStatus === "linking"}
              >
                {notionPageId
                  ? "Linked!"
                  : notionLinkStatus === "error"
                  ? "Link Failed — Retry"
                  : "Link to Notion"}
              </Button>
            )}
          </Group>
        </Alert>

        <Box p="xl">
          <TextInput
            value={categoryName}
            label="Category Name"
            onChange={handleCategoryNameChange}
            mb="xl"
            size="lg"
            styles={{
              label: {
                fontSize: "1.1rem",
                fontWeight: 500,
                marginBottom: "0.5rem",
              },
              input: {
                fontSize: "1.1rem",
                padding: "1rem",
                minHeight: "3.5rem",
              },
            }}
          />

          <DateInput
            label="Start Date"
            value={newStartDate}
            onChange={setNewStartDate}
            mb="md"
            size="lg"
            styles={{
              input: {
                fontSize: "1.1rem",
                padding: "1rem",
              },
            }}
          />
          <DateInput
            label="End Date"
            value={newEndDate}
            onChange={setNewEndDate}
            minDate={newStartDate}
            mb="xl"
            size="lg"
            styles={{
              input: {
                fontSize: "1.1rem",
                padding: "1rem",
              },
            }}
          />

          <Text size="sm" weight={500} mt="md" mb="xs">
            Subcategories
          </Text>
          {subcategoryNames?.map((subcategory, idx) => (
            <Group
              key={subcategory.id}
              position="apart"
              spacing="xs"
              noWrap
              mb="sm"
            >
              {console.log(subcategory)}
              <TextInput
                placeholder="Subcategory Name"
                value={subcategory.name}
                onChange={(event) => handleSubcategoryNameChange(idx, event)}
                style={{ flex: 1 }}
                size="md"
              />
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    size="md"
                    color={getPrivacyColor(subcategory.privacy)}
                  >
                    {subcategory.privacy === "Everyone" && (
                      <IconUsers size={18} />
                    )}
                    {subcategory.privacy === "Only me" && (
                      <IconUser size={18} />
                    )}
                    {subcategory.privacy === "Members" && (
                      <IconUsersGroup size={18} />
                    )}
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Privacy Settings</Menu.Label>
                  <Menu.Item
                    icon={<IconUsers size={16} />}
                    onClick={() =>
                      handleSubcategoryPrivacyChange(idx, "Everyone")
                    }
                  >
                    Everyone
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconUser size={16} />}
                    onClick={() =>
                      handleSubcategoryPrivacyChange(idx, "Only me")
                    }
                  >
                    Only Me
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconUsersGroup size={16} />}
                    onClick={() =>
                      handleSubcategoryPrivacyChange(idx, "Members")
                    }
                  >
                    Members
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Popover position="bottom" shadow="md" withinPortal>
                <Popover.Target>
                  <Button
                    compact
                    variant="subtle"
                    rightIcon={<IconCalendar size={16} />}
                  >
                    Dates
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Text size="sm" weight={500} mb="xs">
                    Start Date
                  </Text>
                  <DateInput
                    value={subcategory.start_date}
                    onChange={(date) =>
                      handleSubcategoryDateChange(idx, "start_date", date)
                    }
                    minDate={newStartDate}
                    maxDate={newEndDate}
                    size="sm"
                    mb="sm"
                  />
                  <Text size="sm" weight={500} mb="xs">
                    End Date
                  </Text>
                  <DateInput
                    value={subcategory.end_date}
                    onChange={(date) =>
                      handleSubcategoryDateChange(idx, "end_date", date)
                    }
                    minDate={subcategory.start_date || newStartDate}
                    maxDate={newEndDate}
                    size="sm"
                  />
                </Popover.Dropdown>
              </Popover>

              <ActionIcon
                onClick={() => handleSubcategoryDelete(idx)}
                color="red"
                variant="light"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}

          {newSubcategoryNames?.map((newSubcategory, idx) => (
            <Group key={idx} position="apart" spacing="xs" noWrap mb="sm">
              <TextInput
                placeholder="Subcategory Name"
                value={newSubcategory.name}
                onChange={(event) =>
                  handleSubcategoryNameChange(idx, event, true)
                }
                style={{ flex: 1 }}
                size="md"
              />
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    size="md"
                    color={getPrivacyColor(newSubcategory.privacy)}
                  >
                    {newSubcategory.privacy === "Everyone" && (
                      <IconUsers size={18} />
                    )}
                    {newSubcategory.privacy === "Only me" && (
                      <IconUser size={18} />
                    )}
                    {newSubcategory.privacy === "Members" && (
                      <IconUsersGroup size={18} />
                    )}
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Privacy Settings</Menu.Label>
                  <Menu.Item
                    icon={<IconUsers size={16} />}
                    onClick={() =>
                      handleSubcategoryPrivacyChange(idx, "Everyone", true)
                    }
                  >
                    Everyone
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconUser size={16} />}
                    onClick={() =>
                      handleSubcategoryPrivacyChange(idx, "Only me", true)
                    }
                  >
                    Only Me
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconUsersGroup size={16} />}
                    onClick={() =>
                      handleSubcategoryPrivacyChange(idx, "Members", true)
                    }
                  >
                    Members
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Popover position="bottom" shadow="md" withinPortal>
                <Popover.Target>
                  <Button
                    compact
                    variant="subtle"
                    rightIcon={<IconCalendar size={16} />}
                  >
                    Dates
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Text size="sm" weight={500} mb="xs">
                    Start Date
                  </Text>
                  <DateInput
                    value={newSubcategory.start_date}
                    onChange={(date) =>
                      handleSubcategoryDateChange(idx, "start_date", date, true)
                    }
                    minDate={newStartDate}
                    maxDate={newEndDate}
                    size="sm"
                    mb="sm"
                  />
                  <Text size="sm" weight={500} mb="xs">
                    End Date
                  </Text>
                  <DateInput
                    value={newSubcategory.end_date}
                    onChange={(date) =>
                      handleSubcategoryDateChange(idx, "end_date", date, true)
                    }
                    minDate={newSubcategory.start_date || newStartDate}
                    maxDate={newEndDate}
                    size="sm"
                  />
                </Popover.Dropdown>
              </Popover>

              <ActionIcon
                onClick={() => handleRemoveNewField(idx)}
                color="red"
                variant="light"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}

          <Button onClick={handleAddNewField} fullWidth mb="xl" size="lg">
            Add New Subcategory
          </Button>

          <Button onClick={handleUpdate} fullWidth size="lg" mb="xl">
            Save Changes
          </Button>
          {error && (
            <div class="mt-2 text-sm text-red-600 bg-red-100 border border-red-300 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          {!isNewCategory && (
            <Button
              onClick={handleDeleteCategory}
              fullWidth
              size="lg"
              color="red"
              variant="outline"
            >
              Delete Category
            </Button>
          )}
        </Box>
      </Drawer>

      <Modal
        opened={deleteConfirmOpened}
        onClose={closeDeleteConfirm}
        title="Confirm Deletion"
        yOffset={80}
        centered
      >
        <Text size="md" mb="xl">
          Are you sure you want to delete the category "{selectedCategory?.name}
          "? This will also delete all subcategories and cannot be undone.
        </Text>
        <Group position="right" spacing="md">
          <Button variant="outline" onClick={closeDeleteConfirm}>
            Cancel
          </Button>
          <Button color="red" onClick={confirmDeleteCategory}>
            Delete Category
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default EditCategoryDrawer;
